import { Link, Navigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ListChecks, Palette, Settings, Shield, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Role, User, page } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { useCan, useCaps } from '../lib/session';
import { Card } from '../components/ui';

const UserPage = page(User);
const RoleList = z.array(Role);

function StatTile({ label, value, loading }: { label: string; value: number | undefined; loading: boolean }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      {loading ? (
        <div className="cs-skeleton h-8 w-16" aria-hidden="true" />
      ) : (
        <p className="text-3xl font-bold tracking-tight text-slate-900">{value ?? '-'}</p>
      )}
      <p className="mt-1 text-sm text-slate-600">{label}</p>
    </div>
  );
}

interface StartItem {
  to: string;
  icon: LucideIcon;
  title: string;
  text: string;
}

function StartCard({ to, icon: Icon, title, text }: StartItem) {
  return (
    <Link
      to={to}
      className="group flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:border-brand"
    >
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand">
        <Icon aria-hidden="true" className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-slate-900">{title}</span>
        <span className="mt-0.5 block text-sm text-slate-600">{text}</span>
      </span>
      <ArrowRight aria-hidden="true" className="mt-1 h-4 w-4 shrink-0 text-slate-500 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

export function GettingStarted() {
  const caps = useCaps();
  const canUsers = useCan('users.view');
  const canSettings = useCan('account.manage');
  const name = caps.tenant ? caps.tenant.branding.displayName || caps.tenant.name : t('app.name');
  const firstName = caps.account.fullName.trim().split(/\s+/)[0] ?? '';

  const users = useQuery({
    queryKey: ['home', 'users'],
    queryFn: () => api.get('/users', { query: { page: 1, pageSize: 1 }, schema: UserPage }),
    enabled: canUsers,
  });
  const invites = useQuery({
    queryKey: ['home', 'invites'],
    queryFn: () => api.get('/users', { query: { page: 1, pageSize: 1, status: 'invited' }, schema: UserPage }),
    enabled: canUsers,
  });
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get('/roles', { schema: RoleList }),
    enabled: canUsers,
  });

  const cards: StartItem[] = [];
  // The set-up guide (Phase 4) appears only while its module is on for this organisation.
  if (canSettings && caps.nav.some((e) => e.path === '/manage/setup')) {
    cards.push({ to: '/manage/setup', icon: ListChecks, title: t('home.start.setup.title'), text: t('home.start.setup.text') });
  }
  if (canSettings) {
    cards.push({ to: '/admin/settings', icon: Palette, title: t('home.start.branding.title'), text: t('home.start.branding.text') });
    cards.push({ to: '/admin/settings', icon: Settings, title: t('home.start.settings.title'), text: t('home.start.settings.text') });
  }
  if (canUsers) {
    cards.push({ to: '/admin/users', icon: Users, title: t('home.start.users.title'), text: t('home.start.users.text') });
    cards.push({ to: '/admin/roles', icon: Shield, title: t('home.start.roles.title'), text: t('home.start.roles.text') });
  }

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-4">
        <h1 className="min-w-0 text-2xl font-bold tracking-tight text-slate-900">{t('home.greeting', { firstName, tenant: name })}</h1>
        {caps.tenant?.logoUrl ? <img src={caps.tenant.logoUrl} alt="" className="h-10 max-w-[10rem] shrink-0 object-contain" /> : null}
      </div>

      {canUsers ? (
        <section aria-label={t('home.stats')} className="mb-8 grid gap-4 sm:grid-cols-3">
          <StatTile label={t('home.stat.users')} value={users.data?.total} loading={users.isPending} />
          <StatTile label={t('home.stat.roles')} value={roles.data?.length} loading={roles.isPending} />
          <StatTile label={t('home.stat.invites')} value={invites.data?.total} loading={invites.isPending} />
        </section>
      ) : null}

      {cards.length > 0 ? (
        <section aria-labelledby="getting-started">
          <h2 id="getting-started" className="text-lg font-semibold text-slate-900">
            {t('home.gettingStarted')}
          </h2>
          <p className="mb-4 mt-1 text-sm text-slate-600">{t('home.gettingStartedHelp')}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {cards.map((c) => (
              <StartCard key={c.title} {...c} />
            ))}
          </div>
        </section>
      ) : (
        <Card>
          <p className="text-slate-600">{t('home.gettingStartedHelp')}</p>
        </Card>
      )}
    </>
  );
}

/**
 * People who can see the Today board land there once that page is in the menu; everyone else gets the
 * getting-started home. (Checking the menu keeps the redirect off until the Today module is switched on.)
 */
export function HomePage() {
  const caps = useCaps();
  const hasToday = caps.permissions.includes('today.view') && caps.nav.some((e) => e.path === '/today');
  if (hasToday) return <Navigate to="/today" replace />;
  return <GettingStarted />;
}
